import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Typography,
  Chip,
  Box,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  IconButton,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import AddIcon from "@material-ui/icons/Add";
import DeleteIcon from "@material-ui/icons/Delete";

import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import toastUtils from "../../utils/toastUtils";

const useStyles = makeStyles((theme) => ({
  root: {
    minWidth: 600,
  },
  formControl: {
    marginBottom: theme.spacing(2),
    minWidth: "100%",
  },
  accordionContent: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(2),
  },
  chipContainer: {
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(0.5),
    marginTop: theme.spacing(1),
  },
  messageSequenceItem: {
    padding: theme.spacing(2),
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    marginBottom: theme.spacing(2),
  },
}));

const SDRCampaignModal = ({ open, onClose, campaignId, onSave }) => {
  const classes = useStyles();
  const [campaign, setCampaign] = useState({
    name: "",
    description: "",
    status: "draft",
    targetCriteria: {
      jobTitles: [],
      location: "Brazil",
      companySizeRanges: [],
      keywords: "",
      industries: [],
      maxResults: 100,
    },
    messageSequence: [
      {
        step: 1,
        type: "initial_contact",
        subject: "",
        content: "",
        delayDays: 0,
      },
    ],
    qualificationRules: {
      minScore: 50,
      requiredFields: ["email"],
      excludeKeywords: [],
    },
    schedulingConfig: {
      enabled: false,
      meetingDuration: 30,
      availableTimes: [],
      bufferTime: 15,
    },
  });
  const [loading, setLoading] = useState(false);
  const [jobTitleInput, setJobTitleInput] = useState("");
  const [industryInput, setIndustryInput] = useState("");
  const [assistants, setAssistants] = useState([]);

  useEffect(() => {
    const fetchCampaign = async () => {
      try {
        setLoading(true);
        const { data } = await api.get(`/sdr/campaigns/${campaignId}`);
        setCampaign(data.data);
      } catch (err) {
        toastError(err);
      } finally {
        setLoading(false);
      }
    };

    const fetchAssistants = async () => {
      try {
        const { data } = await api.get("/assistants");
        setAssistants(data.assistants);
      } catch (err) {
        console.error("Erro ao buscar assistentes:", err);
      }
    };

    const resetCampaign = () => {
      setCampaign({
        name: "",
        description: "",
        status: "draft",
        targetCriteria: {
          jobTitles: [],
          location: "Brazil",
          companySizeRanges: [],
          keywords: "",
          industries: [],
          maxResults: 100,
        },
        messageSequence: [
          {
            step: 1,
            type: "initial_contact",
            subject: "",
            content: "",
            delayDays: 0,
          },
        ],
        qualificationRules: {
          minScore: 50,
          requiredFields: ["email"],
          excludeKeywords: [],
        },
        schedulingConfig: {
          enabled: false,
          meetingDuration: 30,
          availableTimes: [],
          bufferTime: 15,
        },
      });
    };

    if (campaignId) {
      fetchCampaign();
    } else {
      resetCampaign();
    }
    fetchAssistants();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campaignId]);

  const handleInputChange = (field, value) => {
    setCampaign((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleNestedInputChange = (parent, field, value) => {
    setCampaign((prev) => ({
      ...prev,
      [parent]: {
        ...prev[parent],
        [field]: value,
      },
    }));
  };

  const handleSave = async () => {
    try {
      setLoading(true);

      if (!campaign.name.trim()) {
        toastUtils.error(i18n.t("sdr.modal.validation.nameRequired"));
        return;
      }

      let response;
      if (campaignId) {
        response = await api.put(`/sdr/campaigns/${campaignId}`, campaign);
      } else {
        response = await api.post("/sdr/campaigns", campaign);
      }

      onSave(response.data.data);
      toastUtils.success(
        campaignId
          ? i18n.t("sdr.toasts.campaignUpdated")
          : i18n.t("sdr.toasts.campaignCreated")
      );
      onClose();
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  };

  const addJobTitle = () => {
    if (jobTitleInput.trim() && !campaign.targetCriteria.jobTitles.includes(jobTitleInput.trim())) {
      handleNestedInputChange("targetCriteria", "jobTitles", [
        ...campaign.targetCriteria.jobTitles,
        jobTitleInput.trim(),
      ]);
      setJobTitleInput("");
    }
  };

  const removeJobTitle = (title) => {
    handleNestedInputChange(
      "targetCriteria",
      "jobTitles",
      campaign.targetCriteria.jobTitles.filter((t) => t !== title)
    );
  };

  const addIndustry = () => {
    if (industryInput.trim() && !campaign.targetCriteria.industries.includes(industryInput.trim())) {
      handleNestedInputChange("targetCriteria", "industries", [
        ...campaign.targetCriteria.industries,
        industryInput.trim(),
      ]);
      setIndustryInput("");
    }
  };

  const removeIndustry = (industry) => {
    handleNestedInputChange(
      "targetCriteria",
      "industries",
      campaign.targetCriteria.industries.filter((i) => i !== industry)
    );
  };

  const addMessageStep = () => {
    const newStep = {
      step: campaign.messageSequence.length + 1,
      type: "follow_up",
      subject: "",
      content: "",
      delayDays: 3,
    };
    handleInputChange("messageSequence", [...campaign.messageSequence, newStep]);
  };

  const removeMessageStep = (index) => {
    const updatedSequence = campaign.messageSequence.filter((_, i) => i !== index);
    const reorderedSequence = updatedSequence.map((msg, i) => ({
      ...msg,
      step: i + 1,
    }));
    handleInputChange("messageSequence", reorderedSequence);
  };

  const updateMessageStep = (index, field, value) => {
    const updatedSequence = campaign.messageSequence.map((msg, i) =>
      i === index ? { ...msg, [field]: value } : msg
    );
    handleInputChange("messageSequence", updatedSequence);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {campaignId ? i18n.t("sdr.modal.editTitle") : i18n.t("sdr.modal.createTitle")}
      </DialogTitle>
      <DialogContent className={classes.root}>
        <Grid container spacing={3}>
          {/* Informações Básicas */}
          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom>
              {i18n.t("sdr.modal.basicInfo")}
            </Typography>
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label={i18n.t("sdr.modal.name")}
              value={campaign.name}
              onChange={(e) => handleInputChange("name", e.target.value)}
              required
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <InputLabel>{i18n.t("sdr.modal.status")}</InputLabel>
              <Select
                value={campaign.status}
                onChange={(e) => handleInputChange("status", e.target.value)}
              >
                <MenuItem value="draft">{i18n.t("sdr.campaignStatus.draft")}</MenuItem>
                <MenuItem value="active">{i18n.t("sdr.campaignStatus.active")}</MenuItem>
                <MenuItem value="paused">{i18n.t("sdr.campaignStatus.paused")}</MenuItem>
                <MenuItem value="completed">{i18n.t("sdr.campaignStatus.completed")}</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              multiline
              rows={3}
              label={i18n.t("sdr.modal.description")}
              value={campaign.description}
              onChange={(e) => handleInputChange("description", e.target.value)}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <InputLabel>{i18n.t("sdr.modal.assistant")}</InputLabel>
              <Select
                value={campaign.assistantId || ""}
                onChange={(e) => handleInputChange("assistantId", e.target.value)}
              >
                <MenuItem value="">{i18n.t("sdr.modal.noAssistant")}</MenuItem>
                {assistants.map((assistant) => (
                  <MenuItem key={assistant.id} value={assistant.id}>
                    {assistant.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* Critérios de Target */}
          <Grid item xs={12}>
            <Accordion>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="h6">
                  {i18n.t("sdr.modal.targetCriteria")}
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <div className={classes.accordionContent}>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label={i18n.t("sdr.modal.location")}
                        value={campaign.targetCriteria.location}
                        onChange={(e) =>
                          handleNestedInputChange("targetCriteria", "location", e.target.value)
                        }
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        type="number"
                        label={i18n.t("sdr.modal.maxResults")}
                        value={campaign.targetCriteria.maxResults}
                        onChange={(e) =>
                          handleNestedInputChange("targetCriteria", "maxResults", parseInt(e.target.value))
                        }
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label={i18n.t("sdr.modal.keywords")}
                        value={campaign.targetCriteria.keywords}
                        onChange={(e) =>
                          handleNestedInputChange("targetCriteria", "keywords", e.target.value)
                        }
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Box display="flex" alignItems="center" gap={1}>
                        <TextField
                          label={i18n.t("sdr.modal.addJobTitle")}
                          value={jobTitleInput}
                          onChange={(e) => setJobTitleInput(e.target.value)}
                          onKeyPress={(e) => e.key === "Enter" && addJobTitle()}
                        />
                        <IconButton onClick={addJobTitle}>
                          <AddIcon />
                        </IconButton>
                      </Box>
                      <div className={classes.chipContainer}>
                        {campaign.targetCriteria.jobTitles.map((title) => (
                          <Chip
                            key={title}
                            label={title}
                            onDelete={() => removeJobTitle(title)}
                            color="primary"
                            variant="outlined"
                          />
                        ))}
                      </div>
                    </Grid>
                    <Grid item xs={12}>
                      <Box display="flex" alignItems="center" gap={1}>
                        <TextField
                          label={i18n.t("sdr.modal.addIndustry")}
                          value={industryInput}
                          onChange={(e) => setIndustryInput(e.target.value)}
                          onKeyPress={(e) => e.key === "Enter" && addIndustry()}
                        />
                        <IconButton onClick={addIndustry}>
                          <AddIcon />
                        </IconButton>
                      </Box>
                      <div className={classes.chipContainer}>
                        {campaign.targetCriteria.industries.map((industry) => (
                          <Chip
                            key={industry}
                            label={industry}
                            onDelete={() => removeIndustry(industry)}
                            color="secondary"
                            variant="outlined"
                          />
                        ))}
                      </div>
                    </Grid>
                  </Grid>
                </div>
              </AccordionDetails>
            </Accordion>
          </Grid>

          {/* Sequência de Mensagens */}
          <Grid item xs={12}>
            <Accordion>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="h6">
                  {i18n.t("sdr.modal.messageSequence")}
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <div className={classes.accordionContent}>
                  {campaign.messageSequence.map((message, index) => (
                    <div key={index} className={classes.messageSequenceItem}>
                      <Grid container spacing={2} alignItems="center">
                        <Grid item xs={12} sm={3}>
                          <FormControl fullWidth>
                            <InputLabel>{i18n.t("sdr.modal.messageType")}</InputLabel>
                            <Select
                              value={message.type}
                              onChange={(e) => updateMessageStep(index, "type", e.target.value)}
                            >
                              <MenuItem value="initial_contact">
                                {i18n.t("sdr.messageTypes.initial_contact")}
                              </MenuItem>
                              <MenuItem value="follow_up">
                                {i18n.t("sdr.messageTypes.follow_up")}
                              </MenuItem>
                              <MenuItem value="meeting_request">
                                {i18n.t("sdr.messageTypes.meeting_request")}
                              </MenuItem>
                            </Select>
                          </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={3}>
                          <TextField
                            fullWidth
                            type="number"
                            label={i18n.t("sdr.modal.delayDays")}
                            value={message.delayDays}
                            onChange={(e) =>
                              updateMessageStep(index, "delayDays", parseInt(e.target.value))
                            }
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <Box display="flex" justifyContent="flex-end">
                            {campaign.messageSequence.length > 1 && (
                              <IconButton onClick={() => removeMessageStep(index)}>
                                <DeleteIcon />
                              </IconButton>
                            )}
                          </Box>
                        </Grid>
                        <Grid item xs={12}>
                          <TextField
                            fullWidth
                            label={i18n.t("sdr.modal.messageSubject")}
                            value={message.subject}
                            onChange={(e) => updateMessageStep(index, "subject", e.target.value)}
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <TextField
                            fullWidth
                            multiline
                            rows={4}
                            label={i18n.t("sdr.modal.messageContent")}
                            value={message.content}
                            onChange={(e) => updateMessageStep(index, "content", e.target.value)}
                          />
                        </Grid>
                      </Grid>
                    </div>
                  ))}
                  <Button
                    startIcon={<AddIcon />}
                    onClick={addMessageStep}
                    color="primary"
                  >
                    {i18n.t("sdr.modal.addMessage")}
                  </Button>
                </div>
              </AccordionDetails>
            </Accordion>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{i18n.t("common.cancel")}</Button>
        <Button
          onClick={handleSave}
          color="primary"
          variant="contained"
          disabled={loading}
        >
          {loading
            ? i18n.t("common.loading")
            : campaignId
            ? i18n.t("common.update")
            : i18n.t("common.create")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SDRCampaignModal;